import { AriaAttributes, CSSProperties } from 'react';
import { ReactKeyboardEvent, ReactFocusEvent } from '../../react-synthetic-events';
import { TestIdProps } from '../../util/testId';
import { OmitStrict } from '../../utility-types';
import { IconProps } from '../Icon';
import { ItemLocation, MenuItem, MenuSection } from '../Menu';

export enum TokenVariant {
  Default = 'default',
  Primary = 'primary',
  Secondary = 'secondary',
}

export type KeyInteractionType = 'left' | 'right' | 'ctrlHome' | 'ctrlEnd' | undefined;

export interface TokenDesignProps extends TestIdProps {
  /** The label for the token */
  value: string;
  /** An optional icon to be put in front of the token label */
  icon?: OmitStrict<IconProps, 'tokens' | 'ariaHidden' | 'ariaLabel' | 'tooltip'>;
  tooltip?: string;
  ariaLabel?: AriaAttributes['aria-label'];
  ariaDescribedBy?: AriaAttributes['aria-describedby'];
  variant?: TokenVariant | null;
  /**
   * If set to true, no "x" button will be shown
   * @default false
   */
  readOnly?: boolean | null;
  /**
   * If set to true, the token will contain a menu button which opens a menu (specified by `menuItems`) when activated
   *  @default false
   */
  includeDropdown?: boolean | null;

  onClose?: () => void;
  onClick?: () => void;

  /**
   * Handler for onKeyDown.
   *
   * If defined, TokenDesign will use it as keydown handler. If not, a default keydown handler is provided.
   */
  onKeyDown?: (event: ReactKeyboardEvent) => void;
  onFocus?: (event: ReactFocusEvent) => void;
  /**
   * Suppress the focus indicator when focused
   */
  suppressFocusVisible?: boolean;

  style?: CSSProperties;
  className?: string;
  tabIndex?: number;
  /**
   * prop for handling focus in tokenizer
   */
  onTokenFocusBlur?: (focusInteraction: KeyInteractionType) => void;
  /**
   * Determines which key has been pressed, accordingly when focus moves from one token to another
   * (e.g when focus moves from close icon of one token to text of next token on pressing right arrow key),
   * the next token's text or icon  could be focused corresponding to the key that has been pressed
   * and the direction of focus navigation.
   */
  keyInteraction?: KeyInteractionType;
  /**
   *  This is used when a token is deleted, so that new token's text could appropriately receive focus.
   */
  hasTokenCountChanged?: boolean;

  /**
   * Menu items for group token
   * @deprecated use MenuSection instead
   */
  menuItems?: MenuItem[];
  menuSections?: MenuSection[];
  onItemSelected?: (location: ItemLocation[]) => void;
  onCloseItem?: (index: number) => void;
  isFirstToken?: boolean;
  isLastToken?: boolean;
  /**
   * When set to `true`, token is rendered as a `button` element
   * `onClick` will be called when button is either clicked or activated using Enter or Space key
   * Note that token rendered as button is always read-only regardless of `readOnly`
   */
  asButton?: boolean;
  /**
   * @default `props.value`
   */
  id?: string;
  dndPrefix?: string;
  allowDrag?: boolean;
  index?: number;
  closeButtonProps?: TokenCloseButtonProps;
}

export interface TokenCloseButtonProps {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  tooltip?: string;
}

export interface GroupTokenProps
  extends Pick<
    TokenDesignProps,
    | 'onClick'
    | 'tabIndex'
    | 'variant'
    | 'readOnly'
    | 'onClose'
    | 'onFocus'
    | 'testId'
    | 'ariaLabel'
    | 'ariaDescribedBy'
    | 'onCloseItem'
    | 'onTokenFocusBlur'
    | 'keyInteraction'
    | 'hasTokenCountChanged'
    | 'isFirstToken'
    | 'isLastToken'
    | 'closeButtonProps'
  > {
  label: string;
  /**
   * The labels for the menu items when group token is activated
   */
  values: string[];
  className?: string;
  style?: CSSProperties;

  /**
   * The valid values are from 5 to 100.
   *
   * If the value specified is between 1 and 5,
   * then the threshold value is 5.
   * If a value above
   * 100 is specified, then 100 is used for
   * the threshold value.
   * The default threshold value of 100 is used
   * for 0 and negative vlues.
   *
   * If no value is specified then 100 is
   * the default threshold.
   *
   * @default 100
   */
  showMoreThreshold?: number;
  onMorePress?: () => void;
}
